import { RefObject, useEffect, useState } from "react";

interface Containable {
  contains: (target: EventTarget | null) => boolean;
}

/**
 * True once a mousedown lands outside `ref` (and, when given, ALSO outside `secondRef`).
 *
 * `secondRef` is for a menu/panel that is NOT a DOM descendant of `ref` -- a portalled overlay (e.g.
 * rsuite's `Popover`, AvatarMenu.tsx), where a single wrapping ref (every existing caller's own
 * approach, one parent div around both the trigger and an inline menu) can't cover both elements.
 * Omitted, this behaves exactly as before: every existing single-ref caller is unaffected.
 *
 * @param ref - the element outside-click detection is scoped to (a menu's own wrapper, or a
 *   click-menu's trigger).
 * @param secondRef - an optional second element ALSO treated as "inside" (a portalled menu panel).
 */
function useOutsideClick<T extends Containable>(ref: RefObject<T>, secondRef?: RefObject<Containable | null>) {
  const [isOutside, setOutside] = useState(false);

  const handleClickOutside = (event: MouseEvent) => {
    const insideRef = !!ref.current && ref.current.contains(event.target);
    const insideSecondRef = !!secondRef?.current && secondRef.current.contains(event.target);
    setOutside(!!ref.current && !insideRef && !insideSecondRef);
  };

  const setOutsideValue = () => {
    setOutside(!isOutside);
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  });

  return { isOutside, setOutsideValue };
}

export default useOutsideClick;
